import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BoltIcon from '@mui/icons-material/Bolt';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';

const objectives = [
    'Brindar una respuesta rápida y efectiva ante emergencias, minimizando los daños a la propiedad y la pérdida de vidas.',
    'Promover una cultura de seguridad y prevención en nuestra comunidad, a través de campañas de educación y concientización.',
    'Capacitar constantemente a nuestro personal en técnicas y tecnologías de vanguardia, para brindar un servicio de excelencia.',
    'Mantener y mejorar continuamente nuestro equipamiento y tecnología, para estar a la vanguardia en la atención de emergencias.',
    'Desarrollar una red de colaboración con otras entidades y organizaciones, para mejorar la eficiencia y eficacia de la atención de emergencias.',
    'Evaluar y mejorar continuamente nuestros procesos y sistemas, para garantizar la calidad y eficiencia en la prestación de servicios.',
    'Fomentar un ambiente de trabajo seguro y saludable, para el bienestar y satisfacción de nuestro personal y sus familias.',
    'Proveer productos y servicios que brinden a la comunidad seguridad humana, industrial, protección de riesgo contra incendio, otros incidentes y eventos masivos.',
];

const values = [
    'Compromiso',
    'Vocación de servicio',
    'Trabajo en equipo',
    'Responsabilidad',
    'Prevención',
    'Disciplina',
    'Solidaridad',
    'Excelencia',
];

const stats = [
    {
        icon: <LocalFireDepartmentIcon sx={{ fontSize: 34 }} />,
        title: 'Respuesta oportuna',
        description: 'Atención rápida y efectiva frente a emergencias e incidentes.',
    },
    {
        icon: <HealthAndSafetyIcon sx={{ fontSize: 34 }} />,
        title: 'Protección comunitaria',
        description: 'Comprometidos con la seguridad de las personas, bienes y entorno.',
    },
    {
        icon: <WorkspacePremiumIcon sx={{ fontSize: 34 }} />,
        title: 'Servicio de calidad',
        description: 'Mejora continua, capacitación y tecnología para servir mejor.',
    },
];

const SectionTitle = ({ title, subtitle }) => (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
            variant="h3"
            component="h2"
            sx={{
                fontWeight: 700,
                mb: 1,
                color: '#1f1f1f',
            }}
        >
            {title}
        </Typography>

        {subtitle && (
            <Typography
                sx={{
                    maxWidth: '820px',
                    mx: 'auto',
                    color: 'text.secondary',
                    lineHeight: 1.8,
                }}
            >
                {subtitle}
            </Typography>
        )}
    </Box>
);

const InfoCard = ({ icon, title, children }) => (
    <Paper
        elevation={0}
        sx={{
            height: '100%',
            p: 3,
            borderRadius: '24px',
            border: '1px solid rgba(0,0,0,0.08)',
            background: '#fff',
            transition: 'all .3s ease',
            '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 18px 45px rgba(0,0,0,0.10)',
            },
        }}
    >
        <Box
            sx={{
                width: 64,
                height: 64,
                borderRadius: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #d32f2f 0%, #ff6b6b 100%)',
                color: '#fff',
                mb: 2,
            }}
        >
            {icon}
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
        </Typography>

        <Typography
            sx={{
                color: 'text.secondary',
                lineHeight: 1.9,
                textAlign: 'justify',
            }}
        >
            {children}
        </Typography>
    </Paper>
);

const ObjectiveCard = ({ text }) => (
    <Card
        elevation={0}
        sx={{
            height: '100%',
            borderRadius: '22px',
            border: '1px solid rgba(211, 47, 47, 0.12)',
            background: 'linear-gradient(180deg, #ffffff 0%, #fff8f8 100%)',
            transition: 'all .3s ease',
            '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 16px 36px rgba(211, 47, 47, 0.12)',
            },
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    color: '#d32f2f',
                    mb: 2,
                }}
            >
                <GppGoodIcon />
            </Box>

            <Typography
                sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                }}
            >
                {text}
            </Typography>
        </CardContent>
    </Card>
);

const Index = () => {
    return (
        <Box
            sx={{
                background: 'linear-gradient(180deg, #f7f7f7 0%, #ffffff 35%, #f8f8f8 100%)',
                minHeight: '100vh',
                py: { xs: 4, md: 7 },
            }}
        >
            <Container maxWidth="xl">
                {/* HERO */}
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: { xs: '24px', md: '32px' },
                        minHeight: { xs: '380px', md: '470px' },
                        px: { xs: 3, md: 8 },
                        py: { xs: 6, md: 8 },
                        mb: 6,
                        display: 'flex',
                        alignItems: 'center',
                        background: `
                            linear-gradient(135deg, rgba(20,20,20,.88) 0%, rgba(35,35,35,.78) 45%, rgba(211,47,47,.78) 100%),
                            url("https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1600&q=80")
                        `,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        boxShadow: '0 25px 60px rgba(0,0,0,.18)',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -60,
                            right: -60,
                            width: 220,
                            height: 220,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,.08)',
                            filter: 'blur(10px)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -80,
                            left: -60,
                            width: 260,
                            height: 260,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,.06)',
                            filter: 'blur(10px)',
                        }}
                    />

                    <Grid container spacing={4} sx={{ position: 'relative', zIndex: 2 }}>
                        <Grid item xs={12} md={8}>
                            <Chip
                                label="Nosotros"
                                sx={{
                                    mb: 2,
                                    backgroundColor: 'rgba(255,255,255,.12)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    backdropFilter: 'blur(4px)',
                                }}
                            />

                            <Typography
                                variant="h1"
                                component="h1"
                                fontFamily="title-fenabo-font"
                                sx={{
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: { xs: '2.5rem', md: '4.8rem' },
                                    lineHeight: 1,
                                    mb: 2,
                                }}
                            >
                                FENABOCOL
                            </Typography>

                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,.92)',
                                    fontSize: { xs: '1rem', md: '1.15rem' },
                                    lineHeight: 1.9,
                                    maxWidth: '850px',
                                    textAlign: 'justify',
                                }}
                            >
                                ¡No te quemes! En nuestra página web de bomberos encontrarás todo lo que
                                necesitas para mantener tu hogar y comunidad seguros. Desde consejos de
                                prevención de incendios hasta información sobre cómo actuar en caso de
                                emergencia, nuestros expertos están aquí para ayudarte. Además, con nuestra
                                amplia gama de servicios de emergencia, podrás estar seguro de que estamos
                                siempre listos para intervenir y proteger a nuestra comunidad.
                            </Typography>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{ mt: 4 }}
                            >
                                <Chip
                                    icon={<BoltIcon sx={{ color: '#fff !important' }} />}
                                    label="Respuesta inmediata"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: 'rgba(255,255,255,.14)',
                                        height: 40,
                                    }}
                                />
                                <Chip
                                    icon={<GroupsIcon sx={{ color: '#fff !important' }} />}
                                    label="Compromiso comunitario"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: 'rgba(255,255,255,.14)',
                                        height: 40,
                                    }}
                                />
                                <Chip
                                    icon={<MilitaryTechIcon sx={{ color: '#fff !important' }} />}
                                    label="Excelencia en el servicio"
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: 'rgba(255,255,255,.14)',
                                        height: 40,
                                    }}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                {/* BLOQUE DE DESTACADOS */}
                <Grid container spacing={3} sx={{ mb: 7 }}>
                    {stats.map((item, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    borderRadius: '24px',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '18px',
                                        background: 'linear-gradient(135deg, #d32f2f 0%, #ff6b6b 100%)',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2,
                                    }}
                                >
                                    {item.icon}
                                </Box>

                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    {item.title}
                                </Typography>

                                <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                                    {item.description}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* MISIÓN Y VISIÓN */}
                <Grid container spacing={3} sx={{ mb: 7 }}>
                    <Grid item xs={12} md={6}>
                        <InfoCard icon={<LocalFireDepartmentIcon />} title="Misión">
                            Somos una entidad de bomberos comprometida con la protección de vidas y bienes
                            de nuestra comunidad. Trabajamos de manera constante para garantizar que nuestra
                            comunidad se sienta segura y protegida en caso de emergencias. Estamos
                            comprometidos con la excelencia en el servicio, la capacitación continua de
                            nuestro personal y la inversión en tecnología y equipo de última generación para
                            brindar una respuesta rápida y efectiva ante cualquier eventualidad.
                        </InfoCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InfoCard icon={<VisibilityIcon />} title="Visión">
                            Ser reconocidos como una entidad de bomberos líder en la protección de nuestra
                            comunidad, y referente en el país en la prevención y atención de emergencias.
                            Buscamos ser una organización innovadora, comprometida con la mejora continua y
                            la excelencia en el servicio. Aspiramos a ser un modelo a seguir en la gestión
                            eficiente de los recursos y en la promoción de una cultura de seguridad y
                            prevención en nuestra comunidad.
                            <br />
                            <br />
                            También ser para el año 2030 una institución autosuficiente para prestar el
                            servicio público de gestión integral del riesgo contra incendio y calamidades
                            conexas, con más de 100.000 afiliados en nuestra institución.
                        </InfoCard>
                    </Grid>
                </Grid>

                {/* OBJETIVOS */}
                <Box sx={{ mb: 7 }}>
                    <SectionTitle
                        title="Objetivos"
                        subtitle="Cada una de nuestras acciones está orientada a fortalecer la atención de emergencias, la prevención y el bienestar de la comunidad."
                    />

                    <Grid container spacing={3}>
                        {objectives.map((objective, index) => (
                            <Grid item xs={12} sm={6} lg={4} key={index}>
                                <ObjectiveCard text={objective} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* VALORES */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '28px',
                        p: { xs: 3, md: 5 },
                        background: 'linear-gradient(180deg, #ffffff 0%, #fff5f5 100%)',
                        border: '1px solid rgba(211, 47, 47, 0.08)',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            mb: 1,
                            color: '#1f1f1f',
                        }}
                    >
                        Nuestros valores
                    </Typography>

                    <Typography
                        sx={{
                            maxWidth: '700px',
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.8,
                            mb: 4,
                        }}
                    >
                        La vocación de servicio, la disciplina y el compromiso con la comunidad
                        son la base de nuestra labor diaria.
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={2}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent="center"
                    >
                        {values.map((value, index) => (
                            <Chip
                                key={index}
                                label={value}
                                sx={{
                                    backgroundColor: '#fff',
                                    border: '1px solid rgba(211, 47, 47, 0.2)',
                                    color: '#d32f2f',
                                    fontWeight: 500,
                                    px: 1.5,
                                    height: 42,
                                    transition: 'all .2s ease',
                                    '&:hover': {
                                        backgroundColor: '#d32f2f',
                                        color: '#fff',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 16px rgba(211,47,47,0.2)',
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default Index;